import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskText, MaskTextLoad, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { VOICES } from "@/data/content";

const VoiceBlock = ({ v, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "10%"]);
  const reverse = i % 2 === 1;

  return (
    <section ref={ref} id={v.id} data-testid={`voice-${v.id}`} className="relative py-20 md:py-28 px-5 md:px-10 border-b border-ink/10">
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div className={`relative ${reverse ? "md:order-2" : ""}`}>
          <div className="relative overflow-hidden rounded-3xl aspect-[4/5] card-shadow">
            <motion.img style={{ y }} src={v.image} alt={v.label} className="h-[118%] w-full object-cover" />
          </div>
          <span className="absolute -bottom-6 left-4 font-display text-[7rem] md:text-[9rem] leading-none stroke-text-strong pointer-events-none">
            0{i + 1}
          </span>
        </div>

        <div>
          <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta mb-6">{v.label}</p>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.98] text-ink">
            <MaskText lines={[v.heading]} />
          </h2>
          <div className="mt-8 space-y-5 max-w-xl">
            {v.paras.map((p, j) => (
              <FadeUp key={j} delay={0.04 * j}>
                <p className="font-sans text-base md:text-lg leading-relaxed text-ink/70">{p}</p>
              </FadeUp>
            ))}
          </div>

          {v.choices && (
            <Stagger className="mt-10 flex flex-wrap gap-3" stagger={0.06}>
              {v.choices.map((c) => (
                <StaggerItem key={c}>
                  <span className="inline-block rounded-full brand-gradient px-5 py-2.5 font-display text-xs md:text-sm uppercase tracking-[0.1em] font-bold text-white">
                    {c}
                  </span>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </div>
    </section>
  );
};

export default function Voices() {
  return (
    <div className="bg-cream">
      <section className="relative pt-40 md:pt-52 pb-14 px-5 md:px-10 grain overflow-hidden" data-testid="voices-hero">
        <div className="absolute top-10 right-[-4rem] h-[22rem] w-[22rem] rounded-full brand-gradient blur-[130px] opacity-20 animate-floaty" />
        <div className="relative z-10 max-w-[1400px] mx-auto">
          <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta mb-6">Voices</p>
          <h1 className="font-serif font-light text-[13vw] md:text-[7.5vw] leading-[0.86] text-ink">
            <MaskTextLoad lines={["Words for those", "who shape her."]} />
          </h1>
          <FadeUp delay={0.5}>
            <p className="mt-8 max-w-2xl font-sans text-lg md:text-xl leading-relaxed text-ink/65">
              To parents. To schools. To every young woman. Because it takes a community to prepare a future.
            </p>
          </FadeUp>
        </div>
      </section>

      {VOICES.map((v, i) => (
        <VoiceBlock key={v.id} v={v} i={i} />
      ))}

      <section className="py-24 md:py-32 px-5 md:px-10 text-center" data-testid="voices-cta">
        <FadeUp>
          <p className="font-serif italic font-light text-3xl md:text-5xl leading-tight text-ink max-w-4xl mx-auto">
            Never underestimate the influence of a life fully lived.
          </p>
        </FadeUp>
        <FadeUp delay={0.12}>
          <Link
            to="/register"
            data-testid="voices-register"
            className="mt-10 inline-flex rounded-full brand-gradient px-9 py-4 font-display text-sm uppercase tracking-[0.14em] font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(232,38,111,0.4)]"
          >
            Walk Beside Her
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}
